/**
 * Ad-pixel plumbing.
 *
 * TikTok base code is hardcoded in public/index.html (official snippet) so the
 * library is always loaded and TikTok Events Manager always detects the pixel.
 * This module makes sure the PageView actually fires on page load by calling
 * ttq.grantConsent() + ttq.page() AFTER the SDK is initialised (the official
 * snippet's inline call can race the async events.js load).
 *
 * Users can revoke tracking via the GDPR banner → setConsent("denied") calls
 * ttq.revokeConsent() and no further events are sent.
 */
const META_ID = process.env.REACT_APP_META_PIXEL_ID || "";
const TIKTOK_ID = "DB1NF4RC77UEAUE9OV2G";
const CLARITY_ID = "yuik263pbz";
const STORAGE_KEY = "bf_marketing_consent";

let metaLoaded = false;
let tiktokPageFired = false;
let clarityLoaded = false;

/**
 * Microsoft Clarity loader — official snippet, injected on-demand after the
 * cookie banner grants consent (or immediately on module load if the user
 * previously accepted). Idempotent + fully async. ID: yuik263pbz
 */
function loadClarity() {
  if (clarityLoaded || typeof window === "undefined") return;
  clarityLoaded = true;
  try {
    (function(c, l, a, r, i, t, y){
      c[a] = c[a] || function(){ (c[a].q = c[a].q || []).push(arguments); };
      t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
      y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
    })(window, document, "clarity", "script", CLARITY_ID);
    // Hand consent to Clarity so it can start recording immediately.
    try { window.clarity && window.clarity("consent"); } catch {}
  } catch {}
}

/** Safe helpers for Clarity custom tags / events. No-op if not loaded. */
export function claritySet(key, value) {
  try { window.clarity && window.clarity("set", key, String(value)); } catch {}
}
export function clarityEvent(name) {
  try { window.clarity && window.clarity("event", String(name)); } catch {}
}

/**
 * Map TikTok/Meta event names → BookForge funnel-friendly Clarity events.
 * Keeps a single call-site (`track`) across all pixels.
 */
function mapToClarityEvent(name, properties = {}) {
  const cid = properties?.contents?.[0]?.content_id || "";
  if (name === "ViewContent" && cid === "landing") return "landing_view";
  if (name === "ViewContent" && cid.includes("pricing")) return "pricing_view";
  if (name === "ViewContent" && cid.includes("signup")) return "signup_view";
  if (name === "Lead" && cid === "signup_google_clicked") return "google_signup_click";
  if (name === "Lead" && cid === "login_google_clicked") return "google_login_click";
  if (name === "Lead" && cid === "signup_started") return "signup_started";
  if (name === "Lead" && cid.startsWith("click_create_free")) return "click_create_free";
  if (name === "CompleteRegistration") return "signup_completed";
  if (name === "AddToCart") return "pricing_add_to_cart";
  if (name === "AddPaymentInfo") return "add_payment_info";
  if (name === "InitiateCheckout") return "checkout_started";
  if (name === "Purchase") return "purchase_completed";
  return name; // forward raw name as fallback
}

export const consentState = () => {
  try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
};

export const setConsent = (value) => {
  try { localStorage.setItem(STORAGE_KEY, value); } catch {}
  if (value === "granted") enablePixels();
  if (value === "denied") {
    try { window.ttq && window.ttq.revokeConsent && window.ttq.revokeConsent(); } catch {}
    // Clarity: once loaded it can't be fully unloaded, but we stop recording.
    try { window.clarity && window.clarity("stop"); } catch {}
  }
};

const loadScript = (src) => new Promise((resolve, reject) => {
  const s = document.createElement("script");
  s.async = true; s.src = src; s.onload = resolve; s.onerror = reject;
  document.head.appendChild(s);
});

/** Make sure the TikTok PageView fires, retrying briefly until the SDK is ready. */
function fireTikTokPageView() {
  if (tiktokPageFired) return;
  const attempt = (tries) => {
    try {
      if (window.ttq && typeof window.ttq.page === "function") {
        try { window.ttq.grantConsent && window.ttq.grantConsent(); } catch {}
        try { window.ttq.page(); } catch {}
        tiktokPageFired = true;
        return;
      }
    } catch {}
    if (tries > 0) setTimeout(() => attempt(tries - 1), 400);
  };
  attempt(10); // ~4s max of retries
}

export async function enablePixels() {
  fireTikTokPageView();

  // Microsoft Clarity — load after consent, non-blocking.
  loadClarity();

  if (META_ID && !metaLoaded) {
    metaLoaded = true;
    window.fbq = window.fbq || function () {
      window.fbq.callMethod
        ? window.fbq.callMethod.apply(window.fbq, arguments)
        : window.fbq.queue.push(arguments);
    };
    window.fbq.queue = []; window.fbq.version = "2.0";
    window.fbq("consent", "grant");
    try { await loadScript("https://connect.facebook.net/en_US/fbevents.js"); } catch {}
    try { window.fbq("init", META_ID); window.fbq("track", "PageView"); } catch {}
  }
}

export function track(name, properties = {}, eventId) {
  if (consentState() === "denied") return;
  try {
    if (window.fbq) window.fbq("track", name, properties,
      eventId ? { eventID: eventId } : undefined);
    if (window.ttq) window.ttq.track(name, properties,
      eventId ? { event_id: eventId } : undefined);
    // Forward to Microsoft Clarity as a mapped funnel event.
    try {
      const cName = mapToClarityEvent(name, properties);
      const cid = properties?.contents?.[0]?.content_id;
      if (cid) claritySet("funnel_step", cid);
      clarityEvent(cName);
    } catch {}
  } catch (e) { /* noop */ }
}

export const newEventId = () => {
  try { return crypto.randomUUID(); } catch { return `evt_${Date.now()}_${Math.random().toString(36).slice(2)}`; }
};

/**
 * SHA-256 → lowercase hex. TikTok requires PII to be hashed client-side.
 */
export async function sha256(str) {
  try {
    if (!str) return "";
    const normalized = String(str).trim().toLowerCase();
    const buf = new TextEncoder().encode(normalized);
    const hashBuf = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(hashBuf))
      .map(b => b.toString(16).padStart(2, "0")).join("");
  } catch { return ""; }
}

/**
 * TikTok identify(). Call AFTER login/registration on authenticated pages
 * where PII postback is appropriate. Hashes email + external_id with SHA-256.
 * De-duped per session so it doesn't re-fire on every route change.
 */
let lastIdentifyKey = null;
export async function identify({ email, external_id, phone_number } = {}) {
  if (consentState() === "denied") return;
  if (!window.ttq || typeof window.ttq.identify !== "function") return;
  const payload = {};
  if (email) payload.email = await sha256(email);
  if (external_id) payload.external_id = await sha256(external_id);
  if (phone_number) payload.phone_number = phone_number; // caller must hash
  if (!payload.email && !payload.external_id && !payload.phone_number) return;
  const key = JSON.stringify(payload);
  if (key === lastIdentifyKey) return;
  lastIdentifyKey = key;
  try { window.ttq.identify(payload); } catch { /* noop */ }
}

// On module load: unless the user explicitly declined, fire the TikTok PageView
// now that the React bundle (and almost certainly the TikTok SDK) is ready.
// This mirrors the official TikTok snippet behaviour (opt-in by default) while
// avoiding the race condition where ttq.page() runs before events.js has
// finished loading.
if (typeof window !== "undefined") {
  const c = consentState();
  if (c === "denied") {
    try { window.ttq && window.ttq.revokeConsent && window.ttq.revokeConsent(); } catch {}
  } else {
    fireTikTokPageView();
    // Load Clarity at boot only if the user explicitly accepted previously.
    if (c === "granted") {
      loadClarity();
      if (META_ID) enablePixels();
    }
  }
}

export const TIKTOK_PIXEL_ID = TIKTOK_ID;
export const CLARITY_PROJECT_ID = CLARITY_ID;
